import React, { useEffect, useCallback, useState } from 'react';
import { EventResponse, VolunteerResponse } from '@codey2/codey-api-types/models';
import styles from './EventModal.module.css';
import Button from '../../common/Button/Button';
import { TextInput } from '../../common/TextInput/TextInput';
import { TextArea } from '../../common/TextArea/TextArea';

export interface EventFormData {
  title: string;
  description: string;
  startDateTime: string;
  endDateTime: string;
}

export interface EventModalProps {
  state: 'open' | 'closed';
  event?: EventResponse;
  volunteers: VolunteerResponse[];
  onClose: () => void;
  onSave: (data: EventFormData, selectedVolunteerIds: number[]) => Promise<void>;
  isSaving?: boolean;
}

const toDatetimeLocal = (iso: string): string => (iso ? iso.slice(0, 16) : '');
const toISOString = (local: string): string => (local ? `${local}:00Z` : '');

const validate = (form: EventFormData): Partial<Record<keyof EventFormData, string>> => {
  const errors: Partial<Record<keyof EventFormData, string>> = {};
  if (!form.title.trim()) errors.title = 'Titel is verplicht';
  if (!form.startDateTime) errors.startDateTime = 'Startdatum is verplicht';
  if (!form.endDateTime) errors.endDateTime = 'Einddatum is verplicht';
  if (form.startDateTime && form.endDateTime && form.startDateTime >= form.endDateTime) {
    errors.endDateTime = 'Einddatum moet na de startdatum liggen';
  }
  return errors;
};

export const EventModal: React.FC<EventModalProps> = ({ state, event, volunteers, onClose, onSave, isSaving = false }) => {
  const isEditMode = !!event;

  const [form, setForm] = useState<EventFormData>({
    title: '',
    description: '',
    startDateTime: '',
    endDateTime: '',
  });
  const [selectedVolunteerIds, setSelectedVolunteerIds] = useState<Set<number>>(new Set());
  const [errors, setErrors] = useState<Partial<Record<keyof EventFormData, string>>>({});
  const [touched, setTouched] = useState<Partial<Record<keyof EventFormData, boolean>>>({});

  useEffect(() => {
    if (state === 'open') {
      if (event) {
        setForm({
          title: event.title,
          description: event.description,
          startDateTime: toDatetimeLocal(event.startDateTime),
          endDateTime: toDatetimeLocal(event.endDateTime),
        });
        const volunteerIds = (event.volunteers as VolunteerResponse[]).map(v => v.id);
        setSelectedVolunteerIds(new Set(volunteerIds));
      } else {
        setForm({ title: '', description: '', startDateTime: '', endDateTime: '' });
        setSelectedVolunteerIds(new Set());
      }
      setErrors({});
      setTouched({});
    }
  }, [state, event]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && state === 'open') onClose();
    },
    [state, onClose]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    document.body.style.overflow = state === 'open' ? 'hidden' : 'unset';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [state]);

  const setField = (field: keyof EventFormData, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setTouched(prev => ({ ...prev, [field]: true }));
  };

  const toggleVolunteer = (id: number) => {
    setSelectedVolunteerIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSave = async () => {
    const allTouched: Partial<Record<keyof EventFormData, boolean>> = {
      title: true,
      description: true,
      startDateTime: true,
      endDateTime: true,
    };
    setTouched(allTouched);

    const validationErrors = validate(form);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    await onSave(
      { ...form, startDateTime: toISOString(form.startDateTime), endDateTime: toISOString(form.endDateTime) },
      Array.from(selectedVolunteerIds)
    );
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className={`${styles.overlay} ${state === 'open' ? styles.open : ''}`} onClick={handleOverlayClick}>
      <div className={styles.modal} role="dialog" aria-modal="true">
        <div className={styles.header}>
          <h2 className={styles.title}>{isEditMode ? 'Evenement bewerken' : 'Nieuw evenement'}</h2>
        </div>

        <div className={styles.content}>
          {isEditMode && (
            <p className={styles.readonlyNote}>Evenementdetails kunnen niet worden gewijzigd na aanmaak. Pas vrijwilligers hieronder aan.</p>
          )}

          <TextInput
            label="Titel"
            value={form.title}
            onChange={v => setField('title', v)}
            disabled={isEditMode}
            error={errors.title}
            touched={touched.title}
          />

          <TextArea label="Omschrijving" value={form.description} onChange={v => setField('description', v)} disabled={isEditMode} rows={3} />

          <div className={styles.dateRow}>
            <div className={styles.dateField}>
              <label className={styles.dateLabel}>Startdatum &amp; -tijd</label>
              <input
                type="datetime-local"
                value={form.startDateTime}
                onChange={e => setField('startDateTime', e.target.value)}
                disabled={isEditMode}
                className={`${styles.dateInput} ${errors.startDateTime && touched.startDateTime ? styles.dateInputError : ''}
                 ${isEditMode ? styles.dateInputDisabled : ''}`}
              />
              {touched.startDateTime && errors.startDateTime && <span className={styles.fieldError}>{errors.startDateTime}</span>}
            </div>

            <div className={styles.dateField}>
              <label className={styles.dateLabel}>Einddatum &amp; -tijd</label>
              <input
                type="datetime-local"
                value={form.endDateTime}
                onChange={e => setField('endDateTime', e.target.value)}
                disabled={isEditMode}
                className={[
                  styles.dateInput,
                  errors.endDateTime && touched.endDateTime ? styles.dateInputError : '',
                  isEditMode ? styles.dateInputDisabled : '',
                ].join(' ')}
              />
              {touched.endDateTime && errors.endDateTime && <span className={styles.fieldError}>{errors.endDateTime}</span>}
            </div>
          </div>

          <div className={styles.volunteersSection}>
            <h3 className={styles.volunteersTitle}>Vrijwilligers</h3>
            {volunteers.length === 0 ? (
              <p className={styles.noVolunteers}>Geen vrijwilligers beschikbaar.</p>
            ) : (
              <ul className={styles.volunteersList}>
                {volunteers.map(v => (
                  <li key={v.id} className={styles.volunteerItem}>
                    <label className={styles.volunteerLabel}>
                      <input
                        type="checkbox"
                        checked={selectedVolunteerIds.has(v.id)}
                        onChange={() => toggleVolunteer(v.id)}
                        className={styles.checkbox}
                      />
                      <span>{v.name}</span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className={styles.actions}>
          <Button type="secondary" onClick={onClose} disabled={isSaving}>
            Annuleren
          </Button>
          <Button type="primary" onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Opslaan…' : 'Opslaan'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EventModal;
